import { create } from "zustand";
import { useEffect } from "react";

export type TabType = "menu" | "bookmarks" | "community" | "profile";

interface TabNavigationState {
  activeTab: TabType;
  isLargeScreen: boolean;
  largeScreenBreakpoint: number;
  setActiveTab: (tab: TabType) => void;
  resetToMenu: () => void;
  setIsLargeScreen: (isLarge: boolean) => void;
  setLargeScreenBreakpoint: (breakpoint: number) => void;
}

const useTabNavigationStore = create<TabNavigationState>((set, get) => ({
  activeTab: "community",
  isLargeScreen:
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true,
  largeScreenBreakpoint: 1024,
  setActiveTab: (tab: TabType) => set({ activeTab: tab }),
  resetToMenu: () => set({ activeTab: "menu" }),
  setIsLargeScreen: (isLarge: boolean) => {
    const currentState = get();
    set({ isLargeScreen: isLarge });
    if (isLarge && currentState.isLargeScreen) {
      set({ activeTab: "menu" });
    }
  },
  setLargeScreenBreakpoint: (breakpoint: number) => {
    set({ largeScreenBreakpoint: breakpoint });
    if (typeof window !== "undefined") {
      const isLarge = window.innerWidth >= breakpoint;
      get().setIsLargeScreen(isLarge);
    }
  },
}));

export const useTabNavigation = (customBreakpoint?: number) => {
  const store = useTabNavigationStore();
  const {
    activeTab,
    isLargeScreen,
    largeScreenBreakpoint,
    setActiveTab,
    setIsLargeScreen,
    setLargeScreenBreakpoint,
    resetToMenu,
  } = store;

  useEffect(() => {
    if (customBreakpoint && customBreakpoint !== largeScreenBreakpoint) {
      setLargeScreenBreakpoint(customBreakpoint);
    }
  }, [customBreakpoint, largeScreenBreakpoint, setLargeScreenBreakpoint]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const checkScreenSize = () => {
      const isLarge = window.innerWidth >= largeScreenBreakpoint;
      setIsLargeScreen(isLarge);
    };

    checkScreenSize();

    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, [largeScreenBreakpoint, setIsLargeScreen]);

  return {
    activeTab,
    setActiveTab,
    resetToMenu,
    isLargeScreen,
  };
};

export const useActiveTab = () => {
  return useTabNavigationStore((state) => state.activeTab);
};

export const useTabSetter = () => {
  return useTabNavigationStore((state) => state.setActiveTab);
};

export const useScreenSize = () => {
  return useTabNavigationStore((state) => ({
    isLargeScreen: state.isLargeScreen,
    largeScreenBreakpoint: state.largeScreenBreakpoint,
  }));
};
